import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plus, X, Briefcase, ShoppingCart, User, Truck } from "lucide-react";

// ---------------------------------------------------------------------------
// QuickCreateFab — Uygulama geneli hızlı erişim butonu.
//
// Boyut / konum modları:
//   • NORMAL   → 56×56 px, sağdan/alttan 24 px.  Dashboard ve ana liste
//                ekranlarında (Müşteriler, İşler, Tedarikçiler, Satın
//                Almalar, Faturalar, Giderler, Kasalar, Raporlar) kullanılır.
//   • COMPACT  → 44×44 px, sağdan/alttan 14 px.  Detay, alt sayfa ve
//                form ekranlarında kullanılır; hover/focus/aktif menü
//                açılınca 56×56'ya büyür (hem imleç etkileşimi hem menü
//                gösterirken tıklama alanı yeterli olsun diye).
//
// Çakışma önlemi:
//   Sayfanın kendi sağ-alt bölgesinde başka bir aksiyon butonu (örn.
//   "Notları Kaydet") görünüyorsa FAB otomatik olarak yukarı offset
//   uygular (min 12-16 px boşluk).  Tespit ``[data-fab-collision]`` veya
//   ``[data-testid*="save-notes"]`` seçicileriyle yapılır; viewport'un
//   sağ-alt köşesine giriyorlarsa collision-offset devreye girer.
// ---------------------------------------------------------------------------

const ITEMS = [
  { key: "job",      label: "Yeni İş / Satış Oluştur", icon: Briefcase,    path: "/jobs?new=1" },
  { key: "purchase", label: "Yeni Satın Alma Oluştur", icon: ShoppingCart, path: "/purchases?new=1" },
  { key: "customer", label: "Yeni Müşteri Oluştur",    icon: User,         path: "/customers?new=1" },
  { key: "vendor",   label: "Yeni Tedarikçi Oluştur",  icon: Truck,        path: "/vendors?new=1" },
];

// Ana liste rotaları — FAB normal boyutta gösterilir.  Herhangi bir alt
// yol (``/customers/:id``, ``/customers/:id/edit`` vb.) compact kabul edilir.
const MAIN_LIST_PATHS = new Set([
  "/", "/customers", "/jobs", "/vendors", "/purchases",
  "/invoices", "/expenses", "/cash", "/reports",
]);

// Sağ-alt bölgede çakışma yaratabilecek buton seçicileri.  Sayfalar
// isterse ``data-fab-collision`` attribute'u ekleyerek de bildirebilir.
const COLLISION_SELECTORS = [
  '[data-fab-collision]',
  '[data-testid*="save-notes"]',
  '[data-testid$="-save-notes-button"]',
].join(",");

export default function QuickCreateFab() {
  const [open, setOpen] = useState(false);
  const [collisionOffset, setCollisionOffset] = useState(0);
  const ref = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const compact = useMemo(
    () => !MAIN_LIST_PATHS.has(location.pathname),
    [location.pathname],
  );

  // Menü açıldığında compact butonu da genişler (görsel açıdan menüyle
  // bir bütünlük); ayrıca hover/focus ile CSS class üzerinden büyür.
  const enlarged = open;

  // Dışa tıklama + Escape ile kapan
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const key = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  // Çakışma tespiti — sayfa değiştikçe ve scroll/resize'ta yeniden
  // hesaplanır.  Eğer sağ-alt köşede bir aksiyon butonu viewport içine
  // giriyorsa FAB'ı yukarı offset'ler (min 60 px, gerekiyorsa daha fazla).
  useEffect(() => {
    let raf = 0;
    const compute = () => {
      const els = document.querySelectorAll(COLLISION_SELECTORS);
      let maxOverlap = 0;
      const vw = window.innerWidth, vh = window.innerHeight;
      const fabSize = compact && !enlarged ? 44 : 56;
      const fabRight = 24;
      const fabBottom = compact && !enlarged ? 14 : 24;
      // FAB kutusu (viewport koordinatlarında)
      const fabTop = vh - fabBottom - fabSize;
      const fabLeft = vw - fabRight - fabSize;
      const safeGap = 14; // 12-16 px boşluk hedefi
      for (const el of els) {
        const r = el.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) continue; // görünmüyor
        // Yatay ve dikey çakışma
        const overlapsX = r.right > fabLeft - safeGap && r.left < vw - fabRight + safeGap;
        const overlapsY = r.bottom > fabTop - safeGap && r.top < vh - fabBottom + safeGap;
        if (overlapsX && overlapsY) {
          // Ne kadar yukarı çıkmalıyız? Butonun üstünden safeGap kadar yukarı
          const needed = (vh - r.top) + safeGap - fabSize - fabBottom;
          if (needed > maxOverlap) maxOverlap = needed;
        }
      }
      setCollisionOffset(Math.max(0, Math.round(maxOverlap)));
    };
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    // İlk mount + gecikmeli tekrar (DOM içerik async yükleniyorsa yakala)
    schedule();
    const t = setTimeout(schedule, 400);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      clearTimeout(t);
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [location.pathname, compact, enlarged]);

  const select = (path) => {
    setOpen(false);
    navigate(path);
  };

  // Positioning: right/bottom base + collision offset (bottom).
  const baseRight  = compact && !enlarged ? 14 : 24;
  const baseBottom = (compact && !enlarged ? 14 : 24) + collisionOffset;

  // Ana buton boyutu: enlarged/hover/focus ise 56, değilse compact→44
  const btnSize = compact && !enlarged
    ? "w-11 h-11 hover:w-14 hover:h-14 focus-visible:w-14 focus-visible:h-14"
    : "w-14 h-14";

  return (
    <div
      ref={ref}
      className="fixed z-40 flex flex-col items-end gap-2 transition-[right,bottom] duration-200"
      style={{ right: `${baseRight}px`, bottom: `${baseBottom}px` }}
      data-testid="dashboard-quick-fab"
      data-fab-compact={compact ? "1" : "0"}
    >
      {/* Menü — yukarı doğru açılır */}
      {open && (
        <div
          className="mb-1 flex flex-col gap-2 animate-in fade-in slide-in-from-bottom-2 duration-150"
          data-testid="dashboard-quick-fab-menu"
        >
          {ITEMS.map(({ key, label, icon: Icon, path }) => (
            <button
              key={key}
              type="button"
              onClick={() => select(path)}
              className="flex items-center gap-2.5 pl-3 pr-4 py-2.5 rounded-full
                         bg-white border border-slate-200 shadow-md
                         text-sm font-medium text-slate-800
                         hover:bg-slate-900 hover:text-white hover:border-slate-900
                         transition-colors whitespace-nowrap"
              data-testid={`dashboard-quick-fab-${key}`}
            >
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-emerald-50 text-emerald-600">
                <Icon className="w-4 h-4" />
              </span>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Ana + / × butonu — rounded-square, Penz petrol */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Hızlı işlem menüsünü kapat" : "Hızlı işlem menüsünü aç"}
        aria-expanded={open}
        className={`${btnSize} rounded-xl bg-slate-900 hover:bg-slate-800
                    text-white shadow-lg flex items-center justify-center
                    transition-all duration-200 active:scale-95`}
        data-testid="dashboard-quick-fab-toggle"
      >
        {open ? <X className="w-6 h-6" /> : <Plus className="w-6 h-6" />}
      </button>
    </div>
  );
}
